import React, { useState, Fragment } from 'react';
import {
    Box, Card, Typography, Grid, TextField, FormControl,
    InputLabel, Select, MenuItem, Chip, Stack, Paper,
    FormControlLabel, Checkbox, Button, IconButton,
    List, ListItem, ListItemText, ListItemSecondaryAction,
    CircularProgress, Alert, useTheme, alpha, CardContent,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Table, TableBody, TableCell, TableHead, TableRow,
    InputAdornment
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    Delete as DeleteIcon,
    Download as DownloadIcon,
    Visibility as VisibilityIcon,
    InsertDriveFile as FileIcon,
    Description as DescriptionIcon,
    Search as SearchIcon,
    Close as CloseIcon,
    Add as AddIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { supabase } from '../../../../../../supabaseClient';

export default function D2ProblemDescription({ step, onStepUpdate, stepMapping, stepId, products, loadingProducts, companyId, isReadOnly, batchTimeFormat = '12hour' }) {
    const theme = useTheme();
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);

    // Product Filter Dialog States
    const [productFilterDialogOpen, setProductFilterDialogOpen] = useState(false);
    const [productFilterSearchTerm, setProductFilterSearchTerm] = useState('');
    const [dialogSelectedProductFilter, setDialogSelectedProductFilter] = useState(null);
    const [expandedProductsInDialog, setExpandedProductsInDialog] = useState(new Set());

    const updateStep = (updates) => {
        onStepUpdate(stepMapping[stepId], { ...step, ...updates });
    };

    // Product Filter Dialog Handlers
    const handleOpenProductFilterDialog = () => {
        setProductFilterDialogOpen(true);
        setProductFilterSearchTerm('');
        setDialogSelectedProductFilter(undefined);
        setExpandedProductsInDialog(new Set());
    };

    const handleCloseProductFilterDialog = () => {
        setProductFilterDialogOpen(false);
        setProductFilterSearchTerm('');
        setDialogSelectedProductFilter(undefined);
        setExpandedProductsInDialog(new Set());
    };

    const handleDialogProductFilterSelect = (product) => {
        setDialogSelectedProductFilter(product);
    };

    const handleToggleProductExpansion = (productId) => {
        const newExpanded = new Set(expandedProductsInDialog);
        if (newExpanded.has(productId)) {
            newExpanded.delete(productId);
        } else {
            newExpanded.add(productId);
        }
        setExpandedProductsInDialog(newExpanded);
    };

    const handleConfirmProductFilterSelection = () => {
        if (dialogSelectedProductFilter && dialogSelectedProductFilter.type !== 'product') {
            const existingProducts = step.affectedProductsData || [];
            const isAlreadyAdded = existingProducts.some(p => p.uniqueId === dialogSelectedProductFilter.id);
            
            if (!isAlreadyAdded) {
                // Selected a variant, add it directly to affected products
                updateStep({ affectedProductsData: [...existingProducts, {
                    uniqueId: dialogSelectedProductFilter.id,
                    productId: dialogSelectedProductFilter.productId,
                    productName: dialogSelectedProductFilter.parentName,
                    variantName: dialogSelectedProductFilter.name,
                    sku: dialogSelectedProductFilter.sku,
                    productCode: dialogSelectedProductFilter.product_code,
                    packQuantity: dialogSelectedProductFilter.pack_quantity,
                    type: dialogSelectedProductFilter.type,
                    batchNumbers: '',
                    quantity: '',
                    distributionStatus: 'in_production'
                }] });
            }
        }
        
        setProductFilterDialogOpen(false);
        setProductFilterSearchTerm('');
        setDialogSelectedProductFilter(undefined);
        setExpandedProductsInDialog(new Set());
    };

    const getFilteredProductsForFilter = () => {
        if (!productFilterSearchTerm) return getMainProducts() || [];
        
        const searchLower = productFilterSearchTerm.toLowerCase();
        return (getMainProducts() || []).filter(product => 
            product.displayName?.toLowerCase().includes(searchLower) ||
            (product.sku && product.sku.toLowerCase().includes(searchLower)) ||
            (product.name && product.name.toLowerCase().includes(searchLower))
        );
    };

    const uploadFile = async (file) => {
        setUploading(true);
        setUploadError(null);
        
        try {
            const fileExt = file.name.split('.').pop();
            const fileName = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
            const filePath = `${companyId}/evidence/${fileName}`;

            const { data, error } = await supabase.storage
                .from('batch-deviations-evidence')
                .upload(filePath, file);

            if (error) {
                throw error;
            }

            // Get the public URL for the uploaded file
            const { data: { publicUrl } } = supabase.storage
                .from('batch-deviations-evidence')
                .getPublicUrl(filePath);

            // Add the file to the evidence files list
            const newFile = {
                id: fileName,
                name: file.name,
                size: file.size,
                type: file.type,
                path: filePath,
                url: publicUrl,
                uploadedAt: new Date().toISOString()
            };

            const existingFiles = step.evidenceFiles || [];
            updateStep({ 
                evidenceFiles: [...existingFiles, newFile]
            });

        } catch (error) {
            console.error('Error uploading file:', error);
            setUploadError(error.message);
        } finally {
            setUploading(false);
        }
    };

    const deleteFile = async (fileToDelete) => {
        try {
            // Delete from storage
            const { error } = await supabase.storage
                .from('batch-deviations-evidence')
                .remove([fileToDelete.path]);

            if (error) {
                console.error('Error deleting file from storage:', error);
                // Continue to remove from local state even if storage deletion fails
            }

            // Remove from local state
            const updatedFiles = (step.evidenceFiles || []).filter(file => file.id !== fileToDelete.id);
            updateStep({ evidenceFiles: updatedFiles });

        } catch (error) {
            console.error('Error deleting file:', error);
        }
    };

    const downloadFile = async (file) => {
        try {
            const { data, error } = await supabase.storage
                .from('batch-deviations-evidence')
                .download(file.path);

            if (error) {
                throw error;
            }

            // Create blob URL and trigger download
            const url = window.URL.createObjectURL(data);
            const a = document.createElement('a');
            a.href = url;
            a.download = file.name;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

        } catch (error) {
            console.error('Error downloading file:', error);
        }
    };

    const viewFile = async (file) => {
        try {
            const { data, error } = await supabase.storage
                .from('batch-deviations-evidence')
                .createSignedUrl(file.path, 3600); // 1 hour expiry

            if (error) {
                console.error('Error creating signed URL:', error);
                return;
            }

            // Open file in new tab
            window.open(data.signedUrl, '_blank');

        } catch (error) {
            console.error('Error viewing file:', error);
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Filter to get only main products (not variants)
    const getMainProducts = () => {
        return products.filter(product => product.type === 'product');
    };

    // Get variants for a specific product
    const getProductVariants = (productId) => {
        return products.filter(product => 
            (product.type === 'variant_json' && product.productId === productId) ||
            (product.type === 'variant_table' && product.productId === productId)
        );
    };



    // Validation function to check if all required fields are filled
    const isFormValid = () => {
        // Check 5W2H fields
        const has5W2H = step.what?.trim() && 
                       step.who?.trim() && 
                       step.when?.trim() && 
                       step.where?.trim() && 
                       step.how?.trim() && 
                       step.howMuch?.trim();

        // Check impact assessment
        const hasImpactAssessment = step.severityLevel && 
                                   step.urgencyLevel && 
                                   step.customerImpact?.trim();

        // Check financial impact (at least cost impact and financial priority)
        const hasFinancialImpact = step.costImpact !== undefined && step.costImpact !== null && step.costImpact !== '' &&
                                  step.financialPriority;

        // Check affected products (at least one product with required fields)
        const affectedProducts = step.affectedProductsData || [];
        const hasValidProducts = affectedProducts.length > 0 && 
                                affectedProducts.every(product => 
                                    product.productName?.trim() && 
                                    product.batchNumbers?.trim() && 
                                    product.quantity?.trim() && 
                                    product.distributionStatus
                                );

        // Check regulatory risk and recall potential
        const hasRegulatoryInfo = step.regulatoryRisk && step.recallPotential;

        // Check evidence (at least evidence types)
        const hasEvidence = (step.evidenceTypes || []).length > 0;

        return has5W2H && hasImpactAssessment && hasFinancialImpact && 
               hasValidProducts && hasRegulatoryInfo && hasEvidence;
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ 
                p: 4, 
                backgroundColor: alpha(theme.palette.warning.main, 0.1), 
                borderRadius: 4, 
                border: `1px solid ${theme.palette.warning.main}`, 
                boxShadow: `0 8px 32px ${alpha(theme.palette.warning.main, 0.12)}`,
                backdropFilter: 'blur(20px)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha(theme.palette.warning.main, 0.1)}, ${alpha(theme.palette.warning.main, 0.05)})`,
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <DescriptionIcon sx={{ fontSize: 24, color: theme.palette.warning.main }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ 
                            fontWeight: 500, 
                            color: theme.palette.warning.dark, 
                            mb: 0.5
                        }}>
                            D2: Quantifiable Problem Description
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Define the problem in specific, quantifiable terms using the 5W2H methodology (Who, What, When, Where, Why, How, How Much).
                        </Typography>
                    </Box>
                </Box>
                
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ 
                            borderRadius: 4, 
                            border: `1px solid ${theme.palette.grey[400]}`,
                            background: 'white',
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Box sx={{
                                        p: 1.5, 
                                        borderRadius: 2, 
                                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                        backdropFilter: 'blur(10px)',
                                        mr: 2
                                    }}>
                                        <DescriptionIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                            Problem Statement (5W2H)
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Systematic problem analysis
                                        </Typography>
                                    </Box>
                                </Box>
                                
                                <Stack spacing={3} sx={{ flexGrow: 1 }}>
                                    <TextField
                                        fullWidth
                                        label="WHAT is the problem?"
                                        value={step.what || ''}
                                        onChange={isReadOnly ? undefined : (e) => updateStep({ what: e.target.value })}
                                        InputProps={{ readOnly: isReadOnly }}
                                        placeholder="Describe the specific defect, deviation, or issue observed. Include details about appearance, measurements, test results, or any abnormalities."
                                        multiline
                                        rows={3}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                            }
                                        }}
                                    />
                                    <TextField
                                        fullWidth
                                        label="WHO discovered/affected?"
                                        value={step.who || ''}
                                        onChange={isReadOnly ? undefined : (e) => updateStep({ who: e.target.value })}
                                        InputProps={{ readOnly: isReadOnly }}
                                        placeholder="Person who discovered the issue, departments affected, customers impacted, operators involved, etc."
                                        multiline
                                        rows={2}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                            }
                                        }}
                                    />
                                    <TextField
                                        fullWidth
                                        label="WHEN did it occur?"
                                        value={step.when || ''}
                                        onChange={isReadOnly ? undefined : (e) => updateStep({ when: e.target.value })}
                                        InputProps={{ readOnly: isReadOnly }}
                                        placeholder="Specific date and time, shift information, duration, frequency of occurrence, timeline details"
                                        multiline
                                        rows={2}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                            }
                                        }}
                                    />
                                    <TextField
                                        fullWidth
                                        label="WHERE did it happen?"
                                        value={step.where || ''}
                                        onChange={isReadOnly ? undefined : (e) => updateStep({ where: e.target.value })}
                                        InputProps={{ readOnly: isReadOnly }}
                                        placeholder="Production line, equipment location, facility area, specific workstation, geographical location"
                                        multiline
                                        rows={2}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                            }
                                        }}
                                    />
                                    <TextField
                                        fullWidth
                                        label="HOW was it detected?"
                                        value={step.how || ''}
                                        onChange={isReadOnly ? undefined : (e) => updateStep({ how: e.target.value })}
                                        InputProps={{ readOnly: isReadOnly }}
                                        placeholder="Testing method, visual inspection, customer complaint, audit finding, monitoring system alert"
                                        multiline
                                        rows={2}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                            }
                                        }}
                                    />
                                    <TextField
                                        fullWidth
                                        label="HOW MUCH is affected?"
                                        value={step.howMuch || ''}
                                        onChange={isReadOnly ? undefined : (e) => updateStep({ howMuch: e.target.value })}
                                        InputProps={{ readOnly: isReadOnly }}
                                        placeholder="Quantity affected, percentage of batch, number of units, batch numbers, lot numbers, scope of impact"
                                        multiline
                                        rows={2}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                            }
                                        }}
                                    />
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                        <Stack spacing={3}>
                            <Card sx={{ 
                                borderRadius: 4, 
                                border: `1px solid ${theme.palette.grey[400]}`,
                                background: 'white',
                                backdropFilter: 'blur(20px)',
                                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                                },
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}>
                                <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                        <Box sx={{
                                            p: 1.5, 
                                            borderRadius: 2, 
                                            background: `linear-gradient(${alpha(theme.palette.info.main, 0.1)}, ${alpha(theme.palette.info.main, 0.05)})`,
                                            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                                            backdropFilter: 'blur(10px)',
                                            mr: 2
                                        }}>
                                            <DescriptionIcon sx={{ fontSize: 24, color: theme.palette.info.main }} />
                                        </Box>
                                        <Box>
                                            <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                                Impact Assessment
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                Severity and urgency evaluation
                                            </Typography>
                                        </Box>
                                    </Box>
                                    
                                    <Grid container spacing={3} sx={{ flexGrow: 1 }}>
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Severity Level</InputLabel>
                                                <Select
                                                    value={step.severityLevel || ''}
                                                    onChange={isReadOnly ? undefined : (e) => updateStep({ severityLevel: e.target.value })}
                                                    inputProps={{ readOnly: isReadOnly }}
                                                    label="Severity Level"
                                                    sx={{
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: theme.palette.grey[400],
                                                            borderWidth: 1
                                                        },
                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: theme.palette.grey[600],
                                                            borderWidth: 1
                                                        },
                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: theme.palette.grey[700],
                                                            borderWidth: 1
                                                        }
                                                    }}
                                                >
                                                    <MenuItem value="low">Low - Minor impact</MenuItem>
                                                    <MenuItem value="medium">Medium - Moderate impact</MenuItem>
                                                    <MenuItem value="high">High - Significant impact</MenuItem>
                                                    <MenuItem value="critical">Critical - Severe impact</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Urgency Level</InputLabel>
                                                <Select
                                                    value={step.urgencyLevel || ''}
                                                    onChange={isReadOnly ? undefined : (e) => updateStep({ urgencyLevel: e.target.value })}
                                                    inputProps={{ readOnly: isReadOnly }}
                                                    label="Urgency Level"
                                                    sx={{
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: theme.palette.grey[400],
                                                            borderWidth: 1
                                                        },
                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: theme.palette.grey[600],
                                                            borderWidth: 1
                                                        },
                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: theme.palette.grey[700],
                                                            borderWidth: 1
                                                        }
                                                    }}
                                                >
                                                    <MenuItem value="low">Low - Can wait</MenuItem>
                                                    <MenuItem value="medium">Medium - Address soon</MenuItem>
                                                    <MenuItem value="high">High - Immediate attention</MenuItem>
                                                    <MenuItem value="critical">Critical - Emergency</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={4}
                                                label="Customer Impact"
                                                value={step.customerImpact || ''}
                                                onChange={isReadOnly ? undefined : (e) => updateStep({ customerImpact: e.target.value })}
                                                InputProps={{ readOnly: isReadOnly }}
                                                placeholder="Describe how customers are affected. Include details about product performance, safety concerns, compliance issues, user experience, or any customer complaints received."
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                            
                            <Card sx={{ 
                                borderRadius: 4, 
                                border: `1px solid ${theme.palette.grey[400]}`,
                                background: 'white',
                                backdropFilter: 'blur(20px)',
                                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                                },
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}>
                                <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                        <Box sx={{
                                            p: 1.5, 
                                            borderRadius: 2, 
                                            background: `linear-gradient(${alpha(theme.palette.success.main, 0.1)}, ${alpha(theme.palette.success.main, 0.05)})`,
                                            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                                            backdropFilter: 'blur(10px)',
                                            mr: 2
                                        }}>
                                            <DescriptionIcon sx={{ fontSize: 24, color: theme.palette.success.main }} />
                                        </Box>
                                        <Box>
                                            <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                                Financial Impact
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                Cost analysis and financial assessment
                                            </Typography>
                                        </Box>
                                    </Box>
                                    
                                    <Grid container spacing={3} sx={{ flexGrow: 1 }}>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                type="number"
                                                label="Estimated Cost Impact ($)"
                                                fullWidth
                                                value={step.costImpact || ''}
                                                onChange={isReadOnly ? undefined : (e) => updateStep({ costImpact: e.target.value })}
                                                placeholder="0"
                                                InputProps={{
                                                    readOnly: isReadOnly,
                                                    startAdornment: <Typography variant="body2" sx={{ mr: 1 }}>$</Typography>
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                type="number"
                                                label="Affected Batch Value ($)"
                                                fullWidth
                                                value={step.batchValue || ''}
                                                onChange={isReadOnly ? undefined : (e) => updateStep({ batchValue: e.target.value })}
                                                placeholder="0"
                                                InputProps={{
                                                    readOnly: isReadOnly,
                                                    startAdornment: <Typography variant="body2" sx={{ mr: 1 }}>$</Typography>
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                type="number"
                                                label="Recovery/Rework Costs ($)"
                                                fullWidth
                                                value={step.recoveryCosts || ''}
                                                onChange={isReadOnly ? undefined : (e) => updateStep({ recoveryCosts: e.target.value })}
                                                placeholder="0"
                                                InputProps={{
                                                    readOnly: isReadOnly,
                                                    startAdornment: <Typography variant="body2" sx={{ mr: 1 }}>$</Typography>
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                type="number"
                                                label="Potential Lost Revenue ($)"
                                                fullWidth
                                                value={step.lostRevenue || ''}
                                                onChange={isReadOnly ? undefined : (e) => updateStep({ lostRevenue: e.target.value })}
                                                placeholder="0"
                                                InputProps={{
                                                    readOnly: isReadOnly,
                                                    startAdornment: <Typography variant="body2" sx={{ mr: 1 }}>$</Typography>
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Financial Priority Level</InputLabel>
                                                <Select
                                                    value={step.financialPriority || ''}
                                                    onChange={isReadOnly ? undefined : (e) => updateStep({ financialPriority: e.target.value })}
                                                    inputProps={{ readOnly: isReadOnly }}
                                                    label="Financial Priority Level"
                                                    sx={{
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: theme.palette.grey[400],
                                                            borderWidth: 1
                                                        },
                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: theme.palette.grey[600],
                                                            borderWidth: 1
                                                        },
                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: theme.palette.grey[700],
                                                            borderWidth: 1
                                                        }
                                                    }}
                                                >
                                                    <MenuItem value="low">Low (&lt; $10K)</MenuItem>
                                                    <MenuItem value="medium">Medium ($10K - $100K)</MenuItem>
                                                    <MenuItem value="high">High ($100K - $1M)</MenuItem>
                                                    <MenuItem value="critical">Critical (&gt; $1M)</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Insurance Coverage</InputLabel>
                                                <Select
                                                    value={step.insuranceCoverage || ''}
                                                    onChange={isReadOnly ? undefined : (e) => updateStep({ insuranceCoverage: e.target.value })}
                                                    inputProps={{ readOnly: isReadOnly }}
                                                    label="Insurance Coverage"
                                                    sx={{
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: theme.palette.grey[400],
                                                            borderWidth: 1
                                                        },
                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: theme.palette.grey[600],
                                                            borderWidth: 1
                                                        },
                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: theme.palette.grey[700],
                                                            borderWidth: 1
                                                        }
                                                    }}
                                                >
                                                    <MenuItem value="full">Fully Covered</MenuItem>
                                                    <MenuItem value="partial">Partially Covered</MenuItem>
                                                    <MenuItem value="none">Not Covered</MenuItem>
                                                    <MenuItem value="unknown">Coverage Unknown</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormControl fullWidth>
                                                <InputLabel>Cost Categories Affected</InputLabel>
                                                <Select
                                                    multiple
                                                    value={step.costCategories || []}
                                                    onChange={isReadOnly ? undefined : (e) => updateStep({ costCategories: e.target.value })}
                                                    inputProps={{ readOnly: isReadOnly }}
                                                    label="Cost Categories Affected"
                                                    renderValue={(selected) => (
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                            {selected.map((value) => (
                                                                <Chip key={value} label={value} size="small" />
                                                            ))}
                                                        </Box>
                                                    )}
                                                    sx={{
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: theme.palette.grey[400],
                                                            borderWidth: 1
                                                        },
                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: theme.palette.grey[600],
                                                            borderWidth: 1
                                                        },
                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: theme.palette.grey[700],
                                                            borderWidth: 1
                                                        }
                                                    }}
                                                >
                                                    <MenuItem value="Raw Materials">Raw Materials</MenuItem>
                                                    <MenuItem value="Labor">Labor</MenuItem>
                                                    <MenuItem value="Equipment Downtime">Equipment Downtime</MenuItem>
                                                    <MenuItem value="Rework Costs">Rework Costs</MenuItem>
                                                    <MenuItem value="Disposal Costs">Disposal Costs</MenuItem>
                                                    <MenuItem value="Investigation Costs">Investigation Costs</MenuItem>
                                                    <MenuItem value="Customer Credits">Customer Credits</MenuItem>
                                                    <MenuItem value="Regulatory Fines">Regulatory Fines</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Stack>
                    </Grid>
                    
                    <Grid item xs={12}>
                        <Card sx={{ 
                            borderRadius: 4, 
                            border: `1px solid ${theme.palette.grey[400]}`,
                            background: 'white',
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Box sx={{
                                        p: 1.5, 
                                        borderRadius: 2, 
                                        background: `linear-gradient(${alpha('#9c27b0', 0.1)}, ${alpha('#9c27b0', 0.05)})`,
                                        border: `1px solid ${alpha('#9c27b0', 0.2)}`,
                                        backdropFilter: 'blur(10px)',
                                        mr: 2
                                    }}>
                                        <DescriptionIcon sx={{ fontSize: 24, color: '#9c27b0' }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                            Affected Products & Batches
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Product variants and regulatory assessment
                                        </Typography>
                                    </Box>
                                </Box>
                                
                                <Grid container spacing={3} sx={{ flexGrow: 1 }}>
                                    <Grid item xs={12}>
                                        <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
                                            Select Affected Product Variants ({(step.affectedProductsData || []).length} selected)
                                        </Typography>
                                        
                                        {/* Step 1: Select Product to Show Variants */}
                                        <Grid container spacing={2} sx={{ mb: 3 }}>
                                            <Grid item xs={12}>
                                                <Button
                                                    fullWidth
                                                    variant="outlined"
                                                    onClick={handleOpenProductFilterDialog}
                                                    disabled={loadingProducts || isReadOnly}
                                                    sx={{
                                                        justifyContent: 'flex-start',
                                                        textTransform: 'none',
                                                        p: 2,
                                                        fontSize: '1rem',
                                                        fontWeight: 500,
                                                        borderRadius: 3,
                                                        border: `1px solid ${theme.palette.grey[400]}`,
                                                        color: theme.palette.text.secondary,
                                                        backgroundColor: alpha(theme.palette.grey[50], 0.5),
                                                        backdropFilter: 'blur(10px)',
                                                        '&:hover': {
                                                            border: `1px solid ${theme.palette.primary.main}`,
                                                            backgroundColor: alpha(theme.palette.primary.main, 0.05),
                                                            transform: 'translateY(-1px)',
                                                            boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.2)}`
                                                        },
                                                        '&:disabled': {
                                                            color: theme.palette.text.disabled,
                                                            backgroundColor: alpha(theme.palette.grey[200], 0.5)
                                                        }
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                            Add Affected Product Variant
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Click to browse and select product variants
                                                        </Typography>
                                                        {loadingProducts && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                Loading products...
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Button>
                                            </Grid>
                                        </Grid>

                                        {(step.affectedProductsData || []).length > 0 && (
                                            <Stack spacing={2}>
                                                {(step.affectedProductsData || []).map((productData, idx) => (
                                                    <Paper key={idx} sx={{ 
                                                        p: 3, 
                                                        border: `1px solid #9c27b0`, 
                                                        borderRadius: 3,
                                                        backgroundColor: alpha('#9c27b0', 0.05),
                                                        backdropFilter: 'blur(10px)'
                                                    }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                            <Box>
                                                                <Typography variant="h6" sx={{ fontWeight: 500, color: '#9c27b0' }}>
                                                                    {productData.productName}
                                                                    {productData.variantName && (
                                                                        <Chip 
                                                                            label={productData.variantName}
                                                                            size="small"
                                                                            sx={{ ml: 1, backgroundColor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main, fontWeight: 500 }}
                                                                        />
                                                                    )}
                                                                </Typography>
                                                                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        SKU: {productData.sku || 'N/A'}
                                                                    </Typography>
                                                                    {productData.productCode && (
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            • Code: {productData.productCode}
                                                                        </Typography>
                                                                    )}
                                                                    {productData.packQuantity && (
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            • Pack: {productData.packQuantity}
                                                                        </Typography>
                                                                    )}
                                                                    {productData.type !== 'product' && (
                                                                        <Typography variant="caption" sx={{ color: '#9c27b0', fontWeight: 500 }}>
                                                                            • VARIANT
                                                                        </Typography>
                                                                    )}
                                                                </Stack>
                                                            </Box>
                                                            <Button
                                                                size="small"
                                                                color="error"
                                                                onClick={() => {
                                                                    const updatedProducts = (step.affectedProductsData || []).filter(p => p.uniqueId !== productData.uniqueId);
                                                                    updateStep({ affectedProductsData: updatedProducts });
                                                                }}
                                                                sx={{ minWidth: 'auto', p: 0.5 }}
                                                            >
                                                                ✕
                                                            </Button>
                                                        </Box>
                                                        
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12} md={6}>
                                                                <TextField
                                                                    fullWidth
                                                                    multiline
                                                                    rows={3}
                                                                    label="Batch/Lot Numbers"
                                                                    value={productData.batchNumbers || ''}
                                                                    onChange={isReadOnly ? undefined : (e) => {
                                                                        const updatedProducts = (step.affectedProductsData || []).map(p => 
                                                                            p.uniqueId === productData.uniqueId 
                                                                                ? { ...p, batchNumbers: e.target.value }
                                                                                : p
                                                                        );
                                                                        updateStep({ affectedProductsData: updatedProducts });
                                                                    }}
                                                                    InputProps={{ readOnly: isReadOnly }}
                                                                    placeholder="Enter specific batch numbers, lot numbers, date ranges, serial numbers, or any other product identification details..."
                                                                    sx={{
                                                                        '& .MuiOutlinedInput-root': {
                                                                            '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                                            '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                                            '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                                        }
                                                                    }}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12} md={3}>
                                                                <TextField
                                                                    fullWidth
                                                                    label="Quantity Affected"
                                                                    value={productData.quantity || ''}
                                                                    onChange={isReadOnly ? undefined : (e) => {
                                                                        const updatedProducts = (step.affectedProductsData || []).map(p => 
                                                                            p.uniqueId === productData.uniqueId 
                                                                                ? { ...p, quantity: e.target.value }
                                                                                : p
                                                                        );
                                                                        updateStep({ affectedProductsData: updatedProducts });
                                                                    }}
                                                                    InputProps={{ readOnly: isReadOnly }}
                                                                    placeholder="e.g., 500 units, 25 kg, 10 batches"
                                                                    sx={{
                                                                        '& .MuiOutlinedInput-root': {
                                                                            '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                                            '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                                            '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                                        }
                                                                    }}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12} md={3}>
                                                                <FormControl fullWidth>
                                                                    <InputLabel>Distribution Status</InputLabel>
                                                                    <Select
                                                                        value={productData.distributionStatus || 'in_production'}
                                                                        onChange={isReadOnly ? undefined : (e) => {
                                                                            const updatedProducts = (step.affectedProductsData || []).map(p => 
                                                                                p.uniqueId === productData.uniqueId 
                                                                                    ? { ...p, distributionStatus: e.target.value }
                                                                                    : p
                                                                            );
                                                                            updateStep({ affectedProductsData: updatedProducts });
                                                                        }}
                                                                        inputProps={{ readOnly: isReadOnly }}
                                                                        label="Distribution Status"
                                                                        sx={{
                                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                                borderColor: theme.palette.grey[400],
                                                                                borderWidth: 1
                                                                            },
                                                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                                borderColor: theme.palette.grey[600],
                                                                                borderWidth: 1
                                                                            },
                                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                                borderColor: theme.palette.grey[700],
                                                                                borderWidth: 1
                                                                            }
                                                                        }}
                                                                    >
                                                                        <MenuItem value="in_production">In Production</MenuItem>
                                                                        <MenuItem value="in_warehouse">In Warehouse</MenuItem>
                                                                        <MenuItem value="shipped_not_delivered">Shipped Not Delivered</MenuItem>
                                                                        <MenuItem value="delivered_to_customers">Delivered to Customers</MenuItem>
                                                                        <MenuItem value="unknown">Unknown</MenuItem>
                                                                    </Select>
                                                                </FormControl>
                                                            </Grid>
                                                        </Grid>
                                                    </Paper>
                                                ))}
                                            </Stack>
                                        )}
                                    </Grid>
                                    
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>Overall Regulatory Risk</InputLabel>
                                            <Select
                                                value={step.regulatoryRisk || ''}
                                                onChange={isReadOnly ? undefined : (e) => updateStep({ regulatoryRisk: e.target.value })}
                                                inputProps={{ readOnly: isReadOnly }}
                                                label="Overall Regulatory Risk"
                                                sx={{
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: theme.palette.grey[400],
                                                        borderWidth: 1
                                                    },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: theme.palette.grey[600],
                                                        borderWidth: 1
                                                    },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: theme.palette.grey[700],
                                                        borderWidth: 1
                                                    }
                                                }}
                                            >
                                                <MenuItem value="none">No Risk</MenuItem>
                                                <MenuItem value="low">Low Risk</MenuItem>
                                                <MenuItem value="medium">Medium Risk</MenuItem>
                                                <MenuItem value="high">High Risk - Reporting Required</MenuItem>
                                                <MenuItem value="critical">Critical - Immediate Notification</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>Overall Recall Potential</InputLabel>
                                            <Select
                                                value={step.recallPotential || ''}
                                                onChange={isReadOnly ? undefined : (e) => updateStep({ recallPotential: e.target.value })}
                                                inputProps={{ readOnly: isReadOnly }}
                                                label="Overall Recall Potential"
                                                sx={{
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: theme.palette.grey[400],
                                                        borderWidth: 1
                                                    },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: theme.palette.grey[600],
                                                        borderWidth: 1
                                                    },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: theme.palette.grey[700],
                                                        borderWidth: 1
                                                    }
                                                }}
                                            >
                                                <MenuItem value="none">No Recall Risk</MenuItem>
                                                <MenuItem value="voluntary">Voluntary Recall Possible</MenuItem>
                                                <MenuItem value="regulatory">Regulatory Recall Risk</MenuItem>
                                                <MenuItem value="imminent">Imminent Recall Required</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                    
                    <Grid item xs={12}>
                        <Card sx={{ 
                            borderRadius: 4, 
                            border: `1px solid ${theme.palette.grey[400]}`,
                            background: 'white',
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Box sx={{
                                        p: 1.5, 
                                        borderRadius: 2, 
                                        background: `linear-gradient(${alpha(theme.palette.error.main, 0.1)}, ${alpha(theme.palette.error.main, 0.05)})`,
                                        border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                                        backdropFilter: 'blur(10px)',
                                        mr: 2
                                    }}>
                                        <FileIcon sx={{ fontSize: 24, color: theme.palette.error.main }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                            Evidence & Documentation
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            Collect and manage investigation evidence
                                        </Typography>
                                    </Box>
                                </Box>
                                
                                <Grid container spacing={4} sx={{ flexGrow: 1 }}>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth sx={{ mb: 3 }}>
                                            <InputLabel>Evidence Types Collected</InputLabel>
                                            <Select
                                                multiple
                                                value={step.evidenceTypes || []}
                                                onChange={isReadOnly ? undefined : (e) => updateStep({ evidenceTypes: e.target.value })}
                                                inputProps={{ readOnly: isReadOnly }}
                                                label="Evidence Types Collected"
                                                renderValue={(selected) => (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {selected.map((value) => (
                                                            <Chip key={value} label={value} size="small" />
                                                        ))}
                                                    </Box>
                                                )}
                                                sx={{
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: theme.palette.grey[400],
                                                        borderWidth: 1
                                                    },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: theme.palette.grey[600],
                                                        borderWidth: 1
                                                    },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: theme.palette.grey[700],
                                                        borderWidth: 1
                                                    }
                                                }}
                                            >
                                                <MenuItem value="Photographs">Photographs</MenuItem>
                                                <MenuItem value="Test Results">Test Results</MenuItem>
                                                <MenuItem value="Batch Records">Batch Records</MenuItem>
                                                <MenuItem value="Equipment Logs">Equipment Logs</MenuItem>
                                                <MenuItem value="Environmental Data">Environmental Data</MenuItem>
                                                <MenuItem value="Customer Complaints">Customer Complaints</MenuItem>
                                                <MenuItem value="Inspection Reports">Inspection Reports</MenuItem>
                                                <MenuItem value="Video Documentation">Video Documentation</MenuItem>
                                            </Select>
                                        </FormControl>

                                        {/* File Upload Section */}
                                        <Box sx={{ 
                                            border: `2px dashed ${theme.palette.warning.main}`, 
                                            borderRadius: 3, 
                                            p: 2, 
                                            mb: 2,
                                            backgroundColor: alpha(theme.palette.warning.main, 0.05),
                                            backdropFilter: 'blur(10px)'
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                                <CloudUploadIcon sx={{ color: theme.palette.warning.main }} />
                                                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                                    Upload Evidence Files
                                                </Typography>
                                                {uploading && <CircularProgress size={20} />}
                                            </Box>
                                            
                                            <input
                                                type="file"
                                                id="evidence-file-upload"
                                                multiple
                                                accept=".jpg,.jpeg,.png,.gif,.bmp,.tiff,.webp,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip,.mp4,.avi,.mov,.wmv,.flv,.webm,.mkv,.m4v"
                                                style={{ display: 'none' }}
                                                disabled={isReadOnly}
                                                onChange={isReadOnly ? undefined : (e) => {
                                                    const files = Array.from(e.target.files);
                                                    files.forEach(file => {
                                                        uploadFile(file);
                                                    });
                                                    e.target.value = ''; // Reset input
                                                }}
                                            />
                                            
                                            <Button
                                                variant="outlined"
                                                component="label"
                                                htmlFor="evidence-file-upload"
                                                startIcon={<CloudUploadIcon />}
                                                disabled={uploading || isReadOnly}
                                                sx={{ 
                                                    borderColor: theme.palette.warning.main, 
                                                    color: theme.palette.warning.main,
                                                    opacity: isReadOnly ? 0.6 : 1,
                                                    '&:hover': { borderColor: theme.palette.warning.dark },
                                                    borderWidth: 1
                                                }}
                                            >
                                                Choose Files
                                            </Button>
                                            
                                            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                                                Supported: Images (JPG, PNG, GIF, BMP, TIFF, WebP), Videos (MP4, AVI, MOV, WMV, WebM, MKV), PDFs, Documents, Spreadsheets, Text files, ZIP archives (Max 50MB each)
                                            </Typography>
                                        </Box>

                                        {uploadError && (
                                            <Alert severity="error" sx={{ mb: 2 }}>
                                                {uploadError}
                                            </Alert>
                                        )}

                                        {/* Uploaded Files List */}
                                        {(step.evidenceFiles || []).length > 0 && (
                                            <Box sx={{ 
                                                border: `1px solid ${theme.palette.grey[400]}`, 
                                                borderRadius: 3, 
                                                maxHeight: 300, 
                                                overflow: 'auto',
                                                backgroundColor: alpha(theme.palette.grey[50], 0.5),
                                                backdropFilter: 'blur(10px)'
                                            }}>
                                                <Typography variant="subtitle2" sx={{ 
                                                    p: 2, 
                                                    borderBottom: `1px solid ${theme.palette.grey[400]}`, 
                                                    fontWeight: 500 
                                                }}>
                                                    Uploaded Evidence Files ({(step.evidenceFiles || []).length})
                                                </Typography>
                                                <List dense>
                                                    {(step.evidenceFiles || []).map((file, index) => (
                                                        <ListItem key={file.id || index} divider>
                                                            <FileIcon sx={{ mr: 2, color: 'text.secondary' }} />
                                                            <ListItemText
                                                                primary={file.name}
                                                                secondary={`${formatFileSize(file.size)} • ${new Date(file.uploadedAt).toLocaleDateString()}`}
                                                                primaryTypographyProps={{ fontSize: '0.9rem' }}
                                                                secondaryTypographyProps={{ fontSize: '0.8rem' }}
                                                            />
                                                            <ListItemSecondaryAction>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => viewFile(file)}
                                                                    sx={{ mr: 1 }}
                                                                    title="View file in new tab"
                                                                >
                                                                    <VisibilityIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => downloadFile(file)}
                                                                    sx={{ mr: 1 }}
                                                                    title="Download file"
                                                                >
                                                                    <DownloadIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={isReadOnly ? undefined : () => deleteFile(file)}
                                                                    disabled={isReadOnly}
                                                                    color="error"
                                                                    title="Delete file"
                                                                    sx={{ opacity: isReadOnly ? 0.3 : 1 }}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </ListItemSecondaryAction>
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </Box>
                                        )}
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={6}
                                            label="Evidence Location/References"
                                            value={step.evidenceLocation || ''}
                                            onChange={isReadOnly ? undefined : (e) => updateStep({ evidenceLocation: e.target.value })}
                                            InputProps={{ readOnly: isReadOnly }}
                                            placeholder="Document file paths, server locations, physical sample storage locations, reference numbers, database entries, external system IDs, or any other evidence location details..."
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                    '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                    '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                }
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                    
                    <Grid item xs={12}>
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 2, 
                            p: 3, 
                            backgroundColor: 'white',
                            borderRadius: 4, 
                            border: `1px solid ${isFormValid() ? theme.palette.success.main : theme.palette.grey[400]}`,
                            boxShadow: `0 8px 32px ${alpha(isFormValid() ? theme.palette.success.main : theme.palette.grey[500], 0.12)}`,
                            backdropFilter: 'blur(20px)',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={step.completed || false}
                                        onChange={isReadOnly ? undefined : (e) => {
                                            if (!isFormValid() && e.target.checked) {
                                                alert('Please fill out all required fields before marking as complete.');
                                                return;
                                            }
                                            updateStep({ completed: e.target.checked });
                                        }}
                                        disabled={isReadOnly || !isFormValid()}
                                        sx={{ 
                                            '& .MuiSvgIcon-root': { 
                                                fontSize: 28, 
                                                color: isFormValid() ? theme.palette.success.main : theme.palette.grey[400] 
                                            } 
                                        }}
                                    />
                                }
                                label=""
                            />
                            <Typography variant="h6" sx={{ 
                                fontWeight: 500, 
                                color: isFormValid() ? theme.palette.success.main : theme.palette.grey[600] 
                            }}>
                                D2 Problem Description Complete - Ready for Interim Containment
                            </Typography>
                            {!isFormValid() && (
                                <Typography variant="body2" sx={{ color: theme.palette.error.main, fontStyle: 'italic' }}>
                                    (Complete all required fields to enable)
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* Product Filter Dialog */}
            <Dialog
                open={productFilterDialogOpen}
                onClose={handleCloseProductFilterDialog}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        height: '40vh',
                        width: '600px',
                        maxWidth: '600px',
                        margin: 'auto',
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    pb: 2, 
                    fontSize: '1.25rem', 
                    fontWeight: 500,
                    color: 'text.primary',
                    borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
                        Select Product Variant
                    </Typography>
                    <IconButton onClick={handleCloseProductFilterDialog} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
                        <TextField
                            fullWidth
                            placeholder="Search products to find variants..."
                            value={productFilterSearchTerm}
                            onChange={(e) => setProductFilterSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: theme.palette.primary.main }} />
                                    </InputAdornment>
                                ),
                                sx: { 
                                    borderRadius: 3,
                                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                    background: 'white',
                                    '&:hover': {
                                        border: `1px solid ${theme.palette.primary.main}`,
                                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                                    },
                                    '&.Mui-focused': {
                                        border: `1px solid ${theme.palette.primary.main}`,
                                        boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                                    }
                                }
                            }}
                            size="medium"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        border: 'none'
                                    },
                                    '&:hover fieldset': {
                                        border: 'none'
                                    },
                                    '&.Mui-focused fieldset': {
                                        border: 'none'
                                    }
                                }
                            }}
                        />
                    </Box>
                    
                    <Box sx={{ 
                        flex: 1,
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': {
                            display: 'none'
                        },
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none'
                    }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Product & Variants</TableCell>
                                    <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {getFilteredProductsForFilter().map((product) => {
                                    const variants = getProductVariants(product.id);
                                    const isExpanded = expandedProductsInDialog.has(product.id);
                                    
                                    return (
                                        <Fragment key={product.id}>
                                            {/* Main Product Row */}
                                            <TableRow 
                                                hover
                                                sx={{
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        bgcolor: 'grey.50',
                                                        transform: 'translateX(4px)',
                                                        boxShadow: 'inset 4px 0 0 black'
                                                    },
                                                    '& .MuiTableCell-root': {
                                                        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                                                        py: 2,
                                                        pl: 3,
                                                        pr: 3
                                                    }
                                                }}
                                            >
                                                <TableCell onClick={() => handleToggleProductExpansion(product.id)}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        {variants.length > 0 && (
                                                            <IconButton size="small" sx={{ mr: 1, p: 0.5 }}>
                                                                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                            </IconButton>
                                                        )}
                                                        <Box sx={{ ml: variants.length === 0 ? 4 : 0 }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                                                                {product.displayName || product.name}
                                                                {variants.length > 0 && (
                                                                    <Chip 
                                                                        label={`${variants.length} variants`} 
                                                                        size="small" 
                                                                        sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} 
                                                                    />
                                                                )}
                                                            </Typography>
                                                            {product.sku && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    SKU: {product.sku}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="center">
                                                    {variants.length > 0 ? (
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                            Click to expand variants
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                            No variants available
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                            
                                            {/* Variant Rows */}
                                            {isExpanded && variants.map((variant) => (
                                                <TableRow 
                                                    key={variant.id}
                                                    hover
                                                    sx={{
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s ease',
                                                        bgcolor: alpha(theme.palette.grey[50], 0.5),
                                                        '&:hover': {
                                                            bgcolor: alpha(theme.palette.grey[100], 0.8),
                                                            transform: 'translateX(4px)',
                                                            boxShadow: 'inset 4px 0 0 #9c27b0'
                                                        },
                                                        '& .MuiTableCell-root': {
                                                            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.3)}`,
                                                            py: 1.5,
                                                            pl: 3,
                                                            pr: 3
                                                        }
                                                    }}
                                                >
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', pl: 6 }}>
                                                            <Box>
                                                                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem', color: '#9c27b0' }}>
                                                                    {variant.name}
                                                                    <Chip 
                                                                        label="VARIANT" 
                                                                        size="small" 
                                                                        sx={{ 
                                                                            ml: 1, 
                                                                            height: 18, 
                                                                            fontSize: '0.65rem',
                                                                            bgcolor: alpha('#9c27b0', 0.1),
                                                                            color: '#9c27b0'
                                                                        }} 
                                                                    />
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                                    SKU: {variant.sku || 'N/A'}
                                                                    {variant.pack_quantity && ` • Pack: ${variant.pack_quantity}`}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() => handleDialogProductFilterSelect(variant)}
                                                            sx={{
                                                                borderRadius: 2,
                                                                textTransform: 'none',
                                                                fontWeight: 500,
                                                                fontSize: '0.75rem',
                                                                px: 1.5,
                                                                py: 0.5,
                                                                border: `1px solid ${alpha('#9c27b0', 0.8)}`,
                                                                bgcolor: dialogSelectedProductFilter?.id === variant.id ? '#9c27b0' : 'white',
                                                                color: dialogSelectedProductFilter?.id === variant.id ? 'white' : '#9c27b0',
                                                                transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                                                '&:hover': {
                                                                    border: `1px solid #9c27b0`,
                                                                    transform: 'translateY(-1px)',
                                                                    boxShadow: `0 4px 12px ${alpha('#9c27b0', 0.2)}`,
                                                                    bgcolor: dialogSelectedProductFilter?.id === variant.id ? '#9c27b0' : alpha('#9c27b0', 0.1),
                                                                    color: dialogSelectedProductFilter?.id === variant.id ? 'white' : '#9c27b0'
                                                                }
                                                            }}
                                                        >
                                                            Add Variant
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </Fragment>
                                    );
                                })}
                                {getFilteredProductsForFilter().length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {productFilterSearchTerm ? 'No products found matching your search.' : 'No products available.'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ 
                    p: 3, 
                    gap: 2, 
                    borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                    bgcolor: alpha(theme.palette.grey[50], 0.5),
                    display: 'flex',
                    justifyContent: 'flex-end'
                }}>
                    <Button
                        onClick={handleCloseProductFilterDialog}
                        variant="outlined"
                        sx={{ 
                            borderRadius: 2, 
                            px: 3, 
                            py: 1, 
                            fontSize: '0.9rem', 
                            fontWeight: 500,
                            textTransform: 'none',
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            '&:hover': {
                                border: `1px solid ${theme.palette.grey[600]}`,
                                transform: 'translateY(-1px)'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmProductFilterSelection}
                        variant="contained"
                        disabled={!dialogSelectedProductFilter || dialogSelectedProductFilter.type === 'product'}
                        sx={{ 
                            borderRadius: 2, 
                            px: 3, 
                            py: 1, 
                            fontSize: '0.9rem', 
                            fontWeight: 500,
                            textTransform: 'none',
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                            '&:hover': {
                                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                transform: 'translateY(-1px)',
                                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                            },
                            '&:disabled': {
                                background: theme.palette.grey[400],
                                color: theme.palette.grey[600]
                            }
                        }}
                    >
                        Add Variant
                    </Button>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
} 